// Popover (mockup popover(), element 077): a panel next to its button on PC. Esc closes and returns focus to the button,
// a press outside closes it, the page scrolls just enough when there is no room above or below. On phones the callers
// open a sheet instead (see HelpButton, PeriodSeg, Term). The caller owns aria-expanded on its button.
import { useCallback, useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { clamp, cls } from '../lib/util';
import { registerFloating } from './layers';

export interface PopoverProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** returnFocus: true when the person closed it with Esc / ✕ (focus goes back to the button). */
  onClose: (returnFocus: boolean) => void;
  children: ReactNode;
  label?: string;
  role?: string;
  width?: number;
  align?: 'start' | 'end';
  className?: string;
  /** Focus the first control inside on open (default true, like the mockup). */
  autoFocus?: boolean;
}

export function Popover({ anchorRef, open, onClose, children, label, role = 'dialog', width, align = 'end', className, autoFocus = true }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const place = useCallback(() => {
    const pop = ref.current;
    const anchor = anchorRef.current;
    if (!pop || !anchor) return;
    pop.style.maxHeight = '';
    const r = anchor.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = window.innerHeight;
    let left = align === 'start' ? r.left : r.right - pw;
    left = clamp(left, 8, Math.max(8, vw - pw - 8));
    const below = r.bottom + 10;
    const above = r.top - 10 - ph;
    const top = below + ph > vh - 8 && above > 8 ? above : below;
    pop.style.left = `${left + window.scrollX}px`;
    pop.style.top = `${top + window.scrollY}px`;
    pop.style.setProperty('--ox', `${Math.round(clamp(r.left + r.width / 2 - left, 16, Math.max(16, pw - 16)))}px`);
    pop.style.maxHeight = `${Math.max(240, top === below ? vh - below - 12 : r.top - 20)}px`;
  }, [anchorRef, align]);

  useLayoutEffect(() => {
    if (!open) return;
    const pop = ref.current;
    const anchor = anchorRef.current;
    if (!pop || !anchor) return;
    // make room: scroll the page only as much as needed when neither side fits
    const r0 = anchor.getBoundingClientRect();
    const need = Math.min(pop.offsetHeight, window.innerHeight * 0.8);
    const spaceBelow = window.innerHeight - r0.bottom - 18;
    const spaceAbove = r0.top - 18;
    if (spaceBelow < need && spaceAbove < need) {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
      const d = Math.min(need - spaceBelow, maxScroll, Math.max(0, r0.top - 150));
      if (d > 0) window.scrollBy(0, d);
    }
    place();
    if (autoFocus) {
      const first = pop.querySelector<HTMLElement>('input, select, textarea, button:not([disabled]), [tabindex="0"], a[href]');
      (first || pop).focus({ preventScroll: true });
    }
  }, [open, place, anchorRef, autoFocus]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchorRef.current?.contains(t)) return;
      closeRef.current(false);
    };
    const onResize = () => place();
    const tm = setTimeout(() => document.addEventListener('pointerdown', onDown, true), 0);
    window.addEventListener('resize', onResize);
    const unregister = registerFloating(() => closeRef.current(false));
    return () => {
      clearTimeout(tm);
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('resize', onResize);
      unregister();
    };
  }, [open, place, anchorRef]);

  // Esc inside: close and return focus to the button
  useEffect(() => {
    const pop = ref.current;
    if (!open || !pop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      closeRef.current(true);
    };
    pop.addEventListener('keydown', onKey);
    return () => pop.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div ref={ref} className={cls('popover', 'is-open', className)} role={role} aria-label={label} tabIndex={-1} style={width ? { width } : undefined}>
      {children}
    </div>,
    document.body,
  );
}

/** Move focus back to an element if it is still on the page. */
export function focusBack(el: HTMLElement | null | undefined): void {
  if (el && document.contains(el)) el.focus({ preventScroll: true });
}
